import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, cn, HelperText, SearchInput } from '@c2fo/liquidity';
import { InfoCircleIcon } from '@c2fo/liquidity/icons';
import emptystatements from '@/enterprise/assets/emptystatementsgrey.svg?url';
import NoDataState from '@/enterprise/components/NoDataState';
import {
  Table,
  TableBody,
  TableCell,
  TableDisplay,
  TableHead,
  TableHeader,
  TableRow,
  TableSortArrow,
  TableSortDirection,
} from '@/enterprise/components/Table';
import { TakerMarketGroupType } from '@/enterprise/data/useGroupByPricingType';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { Steps } from '@/enterprise/features/recurringRules/utils/recurringRulesFormReducer';
import { RecurringRuleFormError } from '@/enterprise/features/recurringRules/utils/useValidateRuleForm';
import { mapInvoiceRulesToRecurringRule } from '@/enterprise/features/recurringRules/utils/utils';
import sortTakerMarketTable, { Sort, SortKey } from '@/enterprise/features/takerMarketTable/utils/sortTakerMarketTable';
import getMarketType from '@/utils/getMarketType';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useGetRecurringRulesForTakerMarkets from '../utils/useGetRecurringRulesForTakerMarkets';
import useRecurringRuleFormContext from '../utils/useRecurringRuleFormContext';
import Pagination, { getStartEnd } from './Pagination';
import RuleChips from './RuleChips';
import RulePreview from './RulePreview';

interface DivisionSelectTableProps {
  handleStepTo: (step: Steps) => void;
  onSelect: (takerMarkets: TakerMarket[]) => void;
  selectedTakerMarkets: TakerMarket[];
  takerMarkets: TakerMarket[];
  toCurrency: string;
  hasError?: RecurringRuleFormError;
  type?: TakerMarketGroupType;
}

const takerMarketGroupTypeTranslationMap = {
  NAME_YOUR_RATE: 'core.nameYourRateDivisionsSelected',
  BENCHMARK: 'core.variableRateDivisionsSelected',
  PREFERRED: 'core.preferredRateDivisionsSelected',
  FIXED: 'core.fixedRateDivisionsSelected',
  INACTIVE: 'core.inactiveDivisionsSelected',
};

const DivisionSelectTable = ({
  handleStepTo,
  hasError,
  onSelect,
  selectedTakerMarkets,
  takerMarkets = [],
  toCurrency,
  type,
}: DivisionSelectTableProps) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const { getValues } = useRecurringRuleFormContext();
  const [invoiceRules, operatorType] = getValues(['invoiceRules', 'operatorType']);
  const { asNumber } = useLocaleFormat();
  const [search, setSearch] = useState<string>('');
  const [sort, setSort] = useState<Sort>({ key: 'makerDivisionName', direction: 'desc' });
  const { getRecurringRulesForTakerMarkets } = useGetRecurringRulesForTakerMarkets();
  const sortedTakerMarkets = sortTakerMarketTable(takerMarkets, sort);
  const [filteredTakerMarkets, setFilteredTakerMarkets] = useState<TakerMarket[]>(sortedTakerMarkets);

  const filteredCount = filteredTakerMarkets.length;
  const selectedCount = selectedTakerMarkets.length;
  const totalCount = takerMarkets.length;
  const { start, end } = getStartEnd({ limit, page, totalCount });
  const selectedTakerMarketsText = type
    ? t(takerMarketGroupTypeTranslationMap[type], {
        selectedCount: asNumber({ value: selectedCount }),
        totalCount: asNumber({ value: totalCount }),
      })
    : t('core.divisionsSelectedCount', {
        selectedCount: asNumber({ value: selectedCount }),
        totalCount: asNumber({ value: totalCount }),
      });
  const allSelected =
    selectedCount > 0 &&
    filteredCount > 0 &&
    filteredTakerMarkets.every((takerMarket) =>
      selectedTakerMarkets.find(
        (selectedTakerMarket) =>
          selectedTakerMarket.marketUuid === takerMarket.marketUuid &&
          selectedTakerMarket.takerDivisionId === takerMarket.takerDivisionId
      )
    );

  useEffect(() => {
    setFilteredTakerMarkets(
      sortedTakerMarkets.filter((takerMarket) => {
        return (
          getTakerMarketName(takerMarket).toLowerCase().includes(search.toLowerCase()) ||
          getTakerMarketDivisionTitle(takerMarket)?.title.toLowerCase().includes(search.toLowerCase())
        );
      })
    );
  }, [search, sort, sortedTakerMarkets]);

  const onPageSizeChange = (limit: number) => {
    setLimit(limit);
    setPage(1);
  };

  const onSort = (accessorKey: SortKey) => {
    let direction: TableSortDirection = accessorKey === 'makerOrganizationName' ? 'asc' : 'desc';

    if (sort?.key === accessorKey) {
      direction = sort.direction === 'desc' ? 'asc' : 'desc';
    }

    setSort({ key: accessorKey, direction });
  };

  const onSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  // single taker market row is clicked
  const onSelectOne = (takerMarket: TakerMarket) => {
    const exists = selectedTakerMarkets.find(
      (selectedTakerMarket) =>
        selectedTakerMarket.marketUuid === takerMarket.marketUuid &&
        selectedTakerMarket.takerDivisionId === takerMarket.takerDivisionId
    );

    // if already selected, filter from selectedTakerMarkets
    // if not, add to selectedTakerMarkets
    const updated = exists
      ? selectedTakerMarkets.filter(
          (selectedTakerMarket) =>
            selectedTakerMarket.marketUuid !== takerMarket.marketUuid ||
            selectedTakerMarket.takerDivisionId !== takerMarket.takerDivisionId
        )
      : [...selectedTakerMarkets, takerMarket];

    return onSelect(updated);
  };

  // select all checkbox is clicked
  const onSelectAll = (checked: boolean) => {
    // merged selected and currently filtered taker markets
    const allSelected = [...filteredTakerMarkets, ...selectedTakerMarkets];

    if (checked) {
      // remove any duplicates
      const updated = allSelected.filter(
        (takerMarket, index, self) => self.findIndex((s) => s.id === takerMarket.id) === index
      );

      return onSelect(updated);
    } else {
      // get a list of those already selected
      const alreadySelectedTakerMarkets = allSelected.filter(
        (takerMarket, index, self) => self.findIndex((s) => s.id === takerMarket.id) !== index
      );
      // filter out already selected
      const updated = allSelected.filter(
        (takerMarket) =>
          !alreadySelectedTakerMarkets.find(
            (alreadySelectedTakerMarket) =>
              alreadySelectedTakerMarket.marketUuid === takerMarket.marketUuid &&
              alreadySelectedTakerMarket.takerDivisionId === takerMarket.takerDivisionId
          )
      );

      return onSelect(updated);
    }
  };

  return (
    <div>
      <RulePreview
        currency={toCurrency}
        onEdit={() => handleStepTo('criteria')}
        rule={{ ...mapInvoiceRulesToRecurringRule(invoiceRules), operatorType }}
      />
      <div className="my-6 text-sm">{t('recurringRules.selectTheDivisionsInWhichToApplyYourRule')}</div>
      {hasError && (
        <div className="mb-1">
          <HelperText variant="error">{hasError.message}</HelperText>
        </div>
      )}
      <div
        className={cn(
          'flex h-20 items-center justify-between gap-4 rounded-t border border-b-0 border-stroke bg-gray-50 px-4',
          { 'border-red-600': hasError }
        )}
      >
        <div>
          <div>{selectedTakerMarketsText}</div>
          {search !== '' && (
            <div className="font-bold">
              {filteredCount === 1
                ? t('core.divisionsReturned', { value: asNumber({ value: filteredCount }) })
                : t('core.divisionsReturnedPlural', { value: asNumber({ value: filteredCount }) })}
            </div>
          )}
        </div>
        <SearchInput
          aria-label={t('core.search')}
          name="Search"
          onChange={(e) => onSearch(e.target.value)}
          onClear={() => onSearch('')}
          placeholder={t('core.search')}
          size="sm"
          value={search}
        />
      </div>
      <div className={cn('w-full overflow-auto border border-stroke', { 'border-x-red-600': hasError })}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  disabled={filteredCount === 0}
                  color="primary"
                  onChange={(e) => onSelectAll(e.currentTarget.checked)}
                />
              </TableHead>
              <TableHead
                className="max-w-48"
                isSorted={sort?.key === 'makerOrganizationName'}
                onClick={() => onSort('makerOrganizationName')}
              >
                <TableSortArrow accessorKey="makerOrganizationName" sort={sort}>
                  {t('core.division')}
                </TableSortArrow>
              </TableHead>
              <TableHead>{t('recurringRules.existingRules')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTakerMarkets.length === 0 ? (
              <TableDisplay colSpan={4}>
                <NoDataState
                  className="py-8"
                  icon={<img src={emptystatements} alt="emptystatements" className="h-28 w-28" />}
                  title={t('taker.noMatchingDivisions')}
                  message={t('taker.noMatchingDivisionsSearchOnly')}
                />
              </TableDisplay>
            ) : (
              filteredTakerMarkets.slice(start - 1, end).map((takerMarket) => {
                const recurringRule = getRecurringRulesForTakerMarkets([takerMarket])[0];
                const isPreferred = getMarketType(takerMarket) === 'PREFERRED';
                const isSelected = selectedTakerMarkets.some(
                  (selectedTakerMarket) =>
                    selectedTakerMarket.marketUuid === takerMarket.marketUuid &&
                    selectedTakerMarket.takerDivisionId === takerMarket.takerDivisionId
                );

                return (
                  <TableRow key={takerMarket.id}>
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        color="primary"
                        onChange={() => onSelectOne(takerMarket)}
                        disabled={isPreferred}
                      />
                    </TableCell>
                    <TableCell className="w-64">
                      <div className="w-64 truncate">
                        <div className="truncate font-medium" title={getTakerMarketName(takerMarket)}>
                          {getTakerMarketName(takerMarket)}
                        </div>
                        <div
                          className="items-center truncate text-sm text-text-secondary"
                          title={getTakerMarketDivisionTitle(takerMarket)?.title}
                        >
                          {getTakerMarketDivisionTitle(takerMarket)?.content}
                        </div>
                        <div className="text-sm text-text-secondary">{takerMarket.currency}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {recurringRule && (
                        <>
                          <RuleChips recurringRule={recurringRule} withLabel />
                          {isSelected && (
                            <div className="flex gap-1 text-wrap pt-2 text-xs text-red-600">
                              <InfoCircleIcon className="h-4 w-4 shrink-0 fill-red-600" />
                              {t('recurringRules.theExistingRuleWillBeOverriddenByYourNewRule')}
                            </div>
                          )}
                        </>
                      )}
                      {isPreferred && (
                        <div className="flex gap-1 text-wrap pt-2 text-xs text-gray-600">
                          <InfoCircleIcon className="h-4 w-4 shrink-0 fill-text-secondary" />
                          {t('recurringRules.preferredOfferAlert')}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      <div
        className={cn('flex justify-center rounded-b border border-t-0 border-stroke bg-gray-50 p-4', {
          'border-red-600': hasError,
        })}
      >
        <Pagination
          limit={limit}
          onPageChange={setPage}
          onPageSizeChange={onPageSizeChange}
          page={page}
          totalCount={filteredCount}
        />
      </div>
    </div>
  );
};

export default DivisionSelectTable;
